/**
 * PowerHourPanel — Power Hour, version « menu de gauche ».
 *
 * Correction #3 (revue client XDIAL) — refonte complète du comportement et de
 * l'emplacement de la Power Hour. Remplace `PowerHourBadge.tsx`, qui vivait
 * dans la barre d'outils du dialer (sauvegardé dans `avant/`).
 *
 *  - BUG PRIORITAIRE : l'ancien bouton enchaînait `handleManualStartDialing()`
 *    juste après le chrono — un clic sur « Power Hour » composait des numéros.
 *    Supprimé. Le clic ne fait plus que démarrer le compte à rebours.
 *  - DURÉE : saisie libre en minutes (1 à 240), plus seulement 20/30/40/60.
 *  - EMPLACEMENT : le contrôle quitte le dialer et s'installe dans le menu de
 *    gauche (Quick View), sous les onglets de navigation.
 *  - COMPORTEMENT : chrono + bandeau de temps, rien d'autre. Aucune condition
 *    sur les numéros Twilio ni sur les prospects dialables : un chronomètre
 *    n'a besoin ni de l'un ni de l'autre.
 *  - STATS : les résultats du jour (appels, connectés, RDV, temps en ligne)
 *    sont affichés sous le chrono, dans ce même menu.
 *
 * Deux exports :
 *  - PowerHourPanel  → le bloc du menu de gauche (défaut).
 *  - PowerHourBanner → le bandeau de temps, affiché en tête du dialer tant que
 *                      la Power Hour tourne.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDurationClock } from '@/i18n/locale';
import { Flame, Play, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module, Phase 4 cleanup
import { useDialer } from '@/contexts/DialerContext';
import { useDialerGamification } from '@/contexts/DialerGamificationContext';
import { hasMeetingBooked } from '@/utils/callUtils';

// #3 DUREE — plus de liste fermee (20/30/40/60 dans l'ancien badge) : la
// duree se saisit, entre 1 minute et 4 heures.
const MIN_MINUTES = 1;
const MAX_MINUTES = 240;

/** Outcomes où un humain a décroché. Aligné sur ListCallStatsBar. */
const HUMAN_OUTCOMES = [
  'contacted',
  'connected',
  'meeting_booked',
  'callback_requested',
  'not_interested',
  'interested',
];


function formatTalk(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

function clampMinutes(value: number): number {
  if (!Number.isFinite(value)) return 60;
  return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, Math.round(value)));
}

interface DialerCall {
  call_outcome?: string | null;
  duration_seconds?: number | string | null;
  started_at?: string | null;
  created_date?: string | null;
}

/**
 * #3 STATS — résultats du jour, calculés sur les appels du customer courant.
 * « Daily » au sens du client : depuis minuit, pas depuis le démarrage de la
 * session. Un agent qui recharge sa page doit retrouver ses chiffres.
 */
function useDailyStats() {
  const { filteredAllCalls = [] } = useDialer() as { filteredAllCalls?: DialerCall[] };
  return useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const t0 = midnight.getTime();

    let calls = 0;
    let connected = 0;
    let meetings = 0;
    let talkSeconds = 0;

    for (const c of filteredAllCalls) {
      const raw = c.started_at || c.created_date;
      const t = raw ? new Date(raw).getTime() : NaN;
      if (!Number.isFinite(t) || t < t0) continue;
      calls++;
      const outcome = c.call_outcome || '';
      if (hasMeetingBooked(c)) {
        meetings++;
        connected++;
      } else if (HUMAN_OUTCOMES.includes(outcome)) {
        connected++;
      }
      talkSeconds += Number(c.duration_seconds) || 0;
    }
    return { calls, connected, meetings, talkSeconds };
  }, [filteredAllCalls]);
}

interface PowerHourCtx {
  powerHourActive: boolean;
  powerHourCalls: number;
  powerHourGoal: number;
  powerHourDuration: number;
  setPowerHourDuration: (d: number) => void;
  handleTogglePowerHour: (duration?: number) => void;
}

// ── Bloc du menu de gauche ───────────────────────────────────────────────────

export default function PowerHourPanel({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation(['dialer', 'common']);
  const {
    powerHourActive,
    powerHourCalls,
    powerHourGoal,
    powerHourDuration,
    setPowerHourDuration,
    handleTogglePowerHour,
  } = useDialer() as PowerHourCtx;
  // The countdown ticks every second: read from its own context, so that it
  // no longer re-renders every consumer of DialerContext (2026-10-07).
  const { powerHourTimer } = useDialerGamification();

  const daily = useDailyStats();

  // #3 DURÉE — champ de saisie libre. On garde un état local pour laisser
  // l'utilisateur effacer le champ pendant la frappe sans que la préférence
  // persistée ne parte à NaN ; on ne remonte que des valeurs valides.
  const [draft, setDraft] = useState<string>(String(powerHourDuration || 60));
  const editingRef = useRef(false);
  useEffect(() => {
    if (!editingRef.current) setDraft(String(powerHourDuration || 60));
  }, [powerHourDuration]);

  const commit = (raw: string) => {
    editingRef.current = false;
    const next = clampMinutes(parseInt(raw, 10));
    setDraft(String(next));
    setPowerHourDuration(next);
    return next;
  };

  const start = () => {
    const minutes = commit(draft);
    // #3 COMPORTEMENT — un seul appel : le chrono. L'ancien code enchaînait
    // handleManualStartDialing() ici, ce qui lançait les appels.
    handleTogglePowerHour(minutes);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="power-hour-collapsed"
        onClick={() => (powerHourActive ? handleTogglePowerHour() : start())}
        title={
          powerHourActive
            ? t('dialer:powerHour.collapsedActive', { time: formatDurationClock(powerHourTimer) })
            : t('dialer:powerHour.collapsedIdle', { minutes: clampMinutes(parseInt(draft, 10)) })
        }
        className="w-full flex flex-col items-center justify-center"
        style={{
          padding: '6px 0',
          borderRadius: 8,
          border: `1px solid ${powerHourActive ? 'var(--vf-orange)' : 'var(--vf-border)'}`,
          background: powerHourActive ? 'var(--vf-orange-bg)' : 'transparent',
          color: powerHourActive ? 'var(--vf-orange)' : 'var(--vf-text-3)',
          cursor: 'pointer',
          gap: 2,
        }}
      >
        <Flame size={14} className={powerHourActive ? 'vf-pulse' : undefined} />
        {powerHourActive && (
          <span className="vf-font-mono tabular-nums" style={{ fontSize: 9, fontWeight: 700 }}>
            {Math.max(0, Math.ceil(powerHourTimer / 60))}
          </span>
        )}
      </button>
    );
  }

  const pct = powerHourGoal > 0 ? Math.min(100, Math.round((powerHourCalls / powerHourGoal) * 100)) : 0;

  // Le bloc est dense a dessein : il s'installe dans une colonne de 200 px
  // deja occupee par la navigation. Chaque ligne de trop repousse un onglet
  // hors de l'ecran, et le menu se met a defiler. Le gros chrono, lui, vit
  // dans le bandeau (PowerHourBanner) ou il a la place.
  return (
    <div
      data-testid="power-hour-panel"
      style={{
        borderRadius: 10,
        border: `1px solid ${powerHourActive ? 'var(--vf-orange)' : 'var(--vf-border)'}`,
        background: powerHourActive ? 'var(--vf-orange-bg)' : 'var(--vf-surface-2)',
        padding: 7,
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      {/* En-tête — porte le chrono à droite quand la Power Hour tourne. */}
      <div className="flex items-center" style={{ gap: 5 }}>
        <Flame
          size={11}
          className={powerHourActive ? 'vf-pulse' : undefined}
          style={{ color: powerHourActive ? 'var(--vf-orange)' : 'var(--vf-text-3)', flexShrink: 0 }}
        />
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: powerHourActive ? 'var(--vf-orange)' : 'var(--vf-text-3)',
          }}
        >
          {t('dialer:powerHour.start')}
        </span>
        <div className="flex-1" />
        {powerHourActive && (
          <span
            className="vf-font-mono tabular-nums"
            style={{ fontSize: 15, fontWeight: 700, lineHeight: 1, color: 'var(--vf-orange)', letterSpacing: '-0.02em' }}
          >
            {formatDurationClock(powerHourTimer)}
          </span>
        )}
      </div>

      {powerHourActive ? (
        <>
          <div>
            <div className="flex items-center justify-between" style={{ marginBottom: 3 }}>
              <span style={{ fontSize: 9.5, color: 'var(--vf-orange)', opacity: 0.85 }}>{t('dialer:powerHour.timerCalls')}</span>
              <span className="vf-font-mono tabular-nums" style={{ fontSize: 10, fontWeight: 700, color: 'var(--vf-orange)' }}>
                {powerHourCalls}/{powerHourGoal}
              </span>
            </div>
            <div style={{ height: 3, borderRadius: 999, background: 'rgba(234,88,12,0.18)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'var(--vf-orange)', transition: 'width .3s' }} />
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleTogglePowerHour()}
            className="w-full flex items-center justify-center"
            style={{
              gap: 5,
              padding: '4px 0',
              borderRadius: 6,
              border: 'none',
              background: 'rgba(234,88,12,0.16)',
              color: 'var(--vf-orange)',
              fontSize: 10.5,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title={t('dialer:powerHour.stopTitle')}
          >
            <Square size={9} fill="currentColor" />
            {t('dialer:powerHour.stop')}
          </button>
        </>
      ) : (
        <>
          {/* #3 DURÉE — saisie libre en minutes, sur la même ligne que le
              démarrage : le client veut choisir sa durée, pas la prendre dans
              une liste de quatre valeurs. */}
          <div className="flex items-center" style={{ gap: 4 }}>
            <input
              type="number"
              min={MIN_MINUTES}
              max={MAX_MINUTES}
              value={draft}
              aria-label={t('dialer:powerHour.durationAria')}
              onFocus={() => { editingRef.current = true; }}
              onChange={(e) => { editingRef.current = true; setDraft(e.target.value); }}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                  start();
                }
              }}
              className="vf-font-mono tabular-nums"
              style={{
                width: 42,
                padding: '3px 5px',
                borderRadius: 6,
                border: '1px solid var(--vf-border)',
                background: 'var(--vf-surface-1)',
                color: 'var(--vf-text-1)',
                fontSize: 11.5,
                fontWeight: 700,
                textAlign: 'right',
              }}
            />
            <span style={{ fontSize: 10, color: 'var(--vf-text-3)' }}>{t('dialer:powerHour.minutesShort')}</span>
            <button
              type="button"
              onClick={start}
              // Icon only (client request, 2026-09-23): the title and the
              // aria-label carry the word. 2026-10-08 (Kevin): no button
              // shape either, just the orange play icon.
              aria-label={t('dialer:powerHour.startButton')}
              className="flex items-center justify-center"
              style={{
                width: 26,
                height: 22,
                border: 'none',
                background: 'transparent',
                color: 'var(--vf-orange)',
                cursor: 'pointer',
              }}
              title={t('dialer:powerHour.startTitle')}
            >
              <Play size={14} fill="currentColor" />
            </button>
          </div>

        </>
      )}

      {/* #3 STATS — résultats du jour, dans le menu rapide de gauche. */}
      <div style={{ height: 1, background: powerHourActive ? 'rgba(234,88,12,0.2)' : 'var(--vf-border)' }} />
      <div>
        <div className="flex items-baseline" style={{ gap: 4, marginBottom: 2 }}>
          <span
            style={{
              fontSize: 8.5,
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: 'var(--vf-text-3)',
            }}
          >
            {t('common:time.today')}
          </span>
          <div className="flex-1" />
          <span className="vf-font-mono" style={{ fontSize: 9, color: 'var(--vf-text-3)' }}>
            {t('dialer:powerHour.talkTime', { time: formatTalk(daily.talkSeconds) })}
          </span>
        </div>
        <div className="flex items-center justify-between" style={{ gap: 4 }}>
          <DailyStat label={t('dialer:home.stats.calls')} value={daily.calls} color="var(--vf-text-1)" />
          <DailyStat label={t('dialer:home.stats.connected')} value={daily.connected} color="var(--vf-green, #10b981)" />
          <DailyStat label={t('dialer:home.stats.meetings')} value={daily.meetings} color="#15803D" />
        </div>
      </div>
    </div>
  );
}

/**
 * Chiffre + libelle sur UNE ligne : dans une colonne de 200 px partagee avec
 * la navigation, chaque ligne gagnee est un onglet qui reste visible.
 */
function DailyStat({ label, value, color }: { label: string; value: number; color: string }) {
  const { t } = useTranslation('dialer');
  return (
    <span
      className="inline-flex items-baseline"
      style={{ gap: 3, minWidth: 0 }}
      title={t('dialer:powerHour.dailyStatTitle', { value, label: label.toLowerCase() })}
    >
      <span className="vf-font-mono tabular-nums" style={{ fontSize: 12, fontWeight: 700, color, lineHeight: 1.1 }}>
        {value}
      </span>
      <span className="truncate" style={{ fontSize: 9, color: 'var(--vf-text-3)' }}>{label}</span>
    </span>
  );
}

// ── Bandeau de temps ─────────────────────────────────────────────────────────

/**
 * #3 COMPORTEMENT — « le clic démarre uniquement le chrono + le bandeau de
 * temps ». Le bandeau suit l'agent sur toutes les vues du dialer, pas
 * seulement la file d'attente. Il ne porte aucune commande d'appel : juste le
 * temps restant, l'avancement, et de quoi arrêter.
 */
export function PowerHourBanner() {
  const { t } = useTranslation('dialer');
  const {
    powerHourActive,
    powerHourCalls,
    powerHourGoal,
    handleTogglePowerHour,
  } = useDialer() as PowerHourCtx;
  // The countdown ticks every second: read from its own context, so that it
  // no longer re-renders every consumer of DialerContext (2026-10-07).
  const { powerHourTimer } = useDialerGamification();

  if (!powerHourActive) return null;

  const pct = powerHourGoal > 0 ? Math.min(100, Math.round((powerHourCalls / powerHourGoal) * 100)) : 0;

  return (
    <div
      data-testid="power-hour-banner"
      className="flex items-center flex-shrink-0"
      style={{
        gap: 14,
        height: 34,
        padding: '0 20px',
        background: 'var(--vf-orange-bg)',
        borderBottom: '1px solid var(--vf-orange)',
        color: 'var(--vf-orange)',
      }}
    >
      <div className="flex items-center shrink-0" style={{ gap: 7 }}>
        <Flame size={13} className="vf-pulse" />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
          {t('dialer:powerHour.start')}
        </span>
      </div>

      <span className="vf-font-mono tabular-nums" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
        {formatDurationClock(powerHourTimer)}
      </span>

      <div className="flex items-center shrink-0" style={{ gap: 7 }}>
        <span className="vf-font-mono tabular-nums" style={{ fontSize: 11.5, fontWeight: 700 }}>
          {powerHourCalls}/{powerHourGoal}
        </span>
        <div style={{ width: 80, height: 4, borderRadius: 999, background: 'rgba(234,88,12,0.18)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--vf-orange)', transition: 'width .3s' }} />
        </div>
      </div>

      <div className="flex-1" />

      <button
        type="button"
        onClick={() => handleTogglePowerHour()}
        className="flex items-center"
        style={{
          gap: 5,
          padding: '3px 9px',
          borderRadius: 6,
          border: 'none',
          background: 'rgba(234,88,12,0.16)',
          color: 'var(--vf-orange)',
          fontSize: 10.5,
          fontWeight: 600,
          cursor: 'pointer',
        }}
        title={t('dialer:powerHour.stopTitle')}
      >
        <Square size={9} fill="currentColor" />
        {t('dialer:powerHour.stop')}
      </button>
    </div>
  );
}
